import React, { useState, useEffect } from 'react';
import { Plus, Minus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import useAppStore from '../store';
import { EngraisProduct } from '../types';
import SearchableSelect from './SearchableSelect';
import { supabase } from '../lib/supabase';

interface ClientLocation {
  id: string;
  name: string;
  position_lat: number;
  position_lng: number;
  is_primary: boolean;
}

interface EngraisTaskFormProps {
  onSubmit: (task: {
    driverId: string;
    clientId: string;
    locationId: string;
    date: string;
    status?: 'pending' | 'waiting';
    products: EngraisProduct[];
    comment?: string;
  }) => void;
  onCancel: () => void;
}

const EngraisTaskForm: React.FC<EngraisTaskFormProps> = ({ onSubmit, onCancel }) => {
  const { drivers, clients } = useAppStore();
  const [step, setStep] = useState<1 | 2>(1);
  const [clientLocations, setClientLocations] = useState<ClientLocation[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [taskInfo, setTaskInfo] = useState({
    driverId: '',
    clientId: '',
    locationId: '',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    status: 'pending' as 'pending',
    comment: '',
  });

  const [products, setProducts] = useState<EngraisProduct[]>([
    { name: '', plannedQuantityKg: 1 }
  ]);

  useEffect(() => {
    if (taskInfo.clientId) {
      const loadLocations = async () => {
        try {
          const { data, error } = await supabase
            .from('client_locations')
            .select('*')
            .eq('client_id', taskInfo.clientId)
            .order('is_primary', { ascending: false });

          if (error) throw error;

          setClientLocations(data || []);

          const primaryLocation = data?.find(loc => loc.is_primary);
          if (primaryLocation) {
            setTaskInfo(prev => ({ ...prev, locationId: primaryLocation.id }));
          } else {
            setTaskInfo(prev => ({ ...prev, locationId: '' }));
          }
        } catch (error) {
          console.error('Error loading client locations:', error);
          alert('Erreur lors du chargement des points GPS');
        }
      };

      loadLocations();
    } else {
      setClientLocations([]);
      setTaskInfo(prev => ({ ...prev, locationId: '' }));
    }
  }, [taskInfo.clientId]);

  const driverOptions = drivers.map(driver => ({
    value: driver.id,
    label: `${driver.firstName} ${driver.lastName}`
  })).sort((a, b) => {
    const driverA = drivers.find(d => d.id === a.value);
    const driverB = drivers.find(d => d.id === b.value);
    if (!driverA || !driverB) return 0;

    const lastNameComparison = driverA.lastName.localeCompare(driverB.lastName);
    if (lastNameComparison !== 0) return lastNameComparison;
    return driverA.firstName.localeCompare(driverB.firstName);
  });

  const clientOptions = clients.map(client => ({
    value: client.id,
    label: `${client.companyName} (${client.firstName} ${client.lastName})`
  }));

  const addProduct = () => {
    if (products.length < 12) {
      setProducts([...products, { name: '', plannedQuantityKg: 1 }]);
    }
  };

  const removeProduct = (index: number) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  const updateProduct = (index: number, field: keyof EngraisProduct, value: string | number) => {
    const newProducts = [...products];
    newProducts[index] = {
      ...newProducts[index],
      [field]: value
    };
    setProducts(newProducts);
  };

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskInfo.driverId || !taskInfo.clientId || !taskInfo.date || !taskInfo.locationId) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setStep(2);
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validProducts = products.filter(p => p.name.trim() !== '');
    if (validProducts.length === 0) {
      alert('Veuillez ajouter au moins un produit');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        ...taskInfo,
        products: validProducts,
        comment: taskInfo.comment.trim() || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl my-8">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">
            Nouvelle tâche engrais - Étape {step}/2
          </h2>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1Submit} className="flex flex-col">
            <div className="p-8 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-3">
                    Chauffeur
                  </label>
                  <SearchableSelect
                    options={driverOptions}
                    value={taskInfo.driverId}
                    onChange={(value) => setTaskInfo(prev => ({ ...prev, driverId: value }))}
                    placeholder="Sélectionner un chauffeur"
                    searchPlaceholder="Rechercher un chauffeur..."
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-3">
                    Client
                  </label>
                  <SearchableSelect
                    options={clientOptions}
                    value={taskInfo.clientId}
                    onChange={(value) => setTaskInfo(prev => ({ ...prev, clientId: value }))}
                    placeholder="Sélectionner un client"
                    searchPlaceholder="Rechercher un client..."
                  />
                </div>

                {taskInfo.clientId && clientLocations.length > 0 && (
                  <div className="col-span-2">
                    <label className="block text-gray-700 font-medium mb-3">
                      Point GPS
                    </label>
                    <select
                      value={taskInfo.locationId}
                      onChange={(e) => setTaskInfo(prev => ({ ...prev, locationId: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    >
                      <option value="">Sélectionner un point GPS</option>
                      {clientLocations.map(location => (
                        <option key={location.id} value={location.id}>
                          {location.name} {location.is_primary ? '(Principal)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-span-2">
                  <label className="block text-gray-700 font-medium mb-3">
                    Date et heure
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="date"
                      value={taskInfo.date.split('T')[0]}
                      onChange={(e) => {
                        const time = taskInfo.date.split('T')[1] || '07:00';
                        setTaskInfo(prev => ({
                          ...prev,
                          date: `${e.target.value}T${time}`
                        }));
                      }}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                    <select
                      value={taskInfo.date.split('T')[1]?.slice(0, 5) || '07:00'}
                      onChange={(e) => {
                        const date = taskInfo.date.split('T')[0];
                        setTaskInfo(prev => ({
                          ...prev,
                          date: `${date}T${e.target.value}`
                        }));
                      }}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    >
                      {Array.from({ length: 49 }, (_, i) => {
                        const hour = Math.floor(i / 4) + 7;
                        const minute = (i % 4) * 15;
                        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                        return hour < 20 && <option key={time} value={time}>{time}</option>;
                      })}
                    </select>
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="block text-gray-700 font-medium mb-3">
                    Commentaire (facultatif)
                  </label>
                  <textarea
                    value={taskInfo.comment}
                    onChange={(e) => setTaskInfo(prev => ({ ...prev, comment: e.target.value }))}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    rows={3}
                    placeholder="Instructions spécifiques pour le chauffeur..."
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {taskInfo.comment.length}/500 caractères
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
                >
                  Suivant
                </button>
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleStep2Submit} className="flex flex-col">
            <div className="p-8">
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold">Produits ({products.length}/12)</h3>
                  {products.length < 12 && (
                    <button
                      type="button"
                      onClick={addProduct}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-700"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Ajouter un produit
                    </button>
                  )}
                </div>

                <div className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
                  <div className="space-y-4">
                    {products.map((product, index) => (
                      <div key={index} className="flex items-start space-x-4 bg-gray-50 p-6 rounded-lg">
                        <div className="flex-1 grid grid-cols-2 gap-6">
                          <div>
                            <label className="block text-gray-700 font-medium mb-2">
                              Nom du produit {index + 1}
                            </label>
                            <input
                              type="text"
                              value={product.name}
                              onChange={(e) => updateProduct(index, 'name', e.target.value)}
                              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                              placeholder="Nom du produit"
                            />
                          </div>
                          <div className="flex space-x-4 items-end">
                            <div className="flex-1">
                              <label className="block text-gray-700 font-medium mb-2">
                                Quantité prévue (kg)
                              </label>
                              <input
                                type="number"
                                value={product.plannedQuantityKg}
                                onChange={(e) => updateProduct(index, 'plannedQuantityKg', parseFloat(e.target.value))}
                                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                min="0.1"
                                step="0.1"
                              />
                            </div>
                            {products.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeProduct(index)}
                                className="text-red-600 hover:text-red-800 p-3 h-[50px]"
                                title="Supprimer ce produit"
                              >
                                <Minus className="h-6 w-6" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={isSubmitting}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  <span>{isSubmitting ? 'Création en cours...' : 'Créer la tâche'}</span>
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EngraisTaskForm;
